import { Box, Chip, MenuItem, Select, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import type { JSX } from "react";

import type { SalesSteerMode, SalesSteerPreview } from "@xop/dashboard-shared";

/**
 * "This will change the live campaign: …" — the diff shown ABOVE the Send
 * button in the Harvester steer box.
 *
 * ## Why it exists
 *
 * The harvester used to read a steering note on every beat as a CONSTRAINT
 * over its target matrix, from a closed keyword vocabulary that failed open.
 * On 2026-08-06 the CEO typed "investments banks, private wire networks,
 * trader voice networks in USA and Europe": the two place words narrowed the
 * sweep and all three sectors were discarded, because extraction only began at
 * a trigger word ("hunt" / "only" / "target" / …). Nothing said so. The note
 * read as accepted.
 *
 * A note now EDITS the live campaign, and this panel is the honesty half of
 * that: the operator sees what their sentence resolved to before it lands. It
 * costs no extra click — it renders above the button they were already going
 * to press — so the flow is still one field, one button.
 *
 * The `replace | add to` picker is deliberately the ONE control here. Whether
 * "investment banks, private wire networks" replaces the campaign's five
 * current sectors or joins them is the single thing a machine genuinely can't
 * know, and it is the difference between a redirected sweep and a widened one.
 */
export function SteerCampaignDiff({
  preview,
  mode,
  onModeChange,
}: {
  preview: SalesSteerPreview;
  mode: SalesSteerMode;
  onModeChange: (next: SalesSteerMode) => void;
}): JSX.Element {
  const { sectors, regions, goal, campaign } = preview;
  // Recomputed against the CHOSEN mode: switching to "add to" must visibly
  // un-remove the sectors a replace would have dropped, or the picker looks
  // inert until the operator sends.
  const removed = mode === "add" ? [] : sectors.removed;

  return (
    <Box
      aria-label="what this will change"
      sx={{
        display: "grid",
        gap: 1,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "surfaceSoft",
        px: 1.75,
        py: 1.5,
      }}
    >
      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
        {campaign.is_new ? (
          <>
            This will <strong>create</strong> the live campaign:
          </>
        ) : (
          <>
            This will change the live campaign <strong>{campaign.label}</strong>:
          </>
        )}
      </Typography>

      <DiffRow label="Sectors">
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
          {sectors.added.map((s) => (
            <SectorChip key={`add-${s.vertical}`} label={s.label} kind="added" />
          ))}
          {sectors.kept.map((s) => (
            <SectorChip key={`keep-${s.vertical}`} label={s.label} kind="kept" />
          ))}
          {removed.map((s) => (
            <SectorChip key={`rm-${s.vertical}`} label={s.label} kind="removed" />
          ))}
          {sectors.added.length === 0 &&
          sectors.removed.length === 0 &&
          sectors.kept.length === 0 ? (
            <Muted>unchanged</Muted>
          ) : null}
          {sectors.added.length > 0 && !campaign.is_new ? (
            <Select
              size="small"
              value={mode}
              onChange={(e) => onModeChange(e.target.value as SalesSteerMode)}
              // On the DISPLAY element, not the hidden native input — that is
              // the `role="combobox"` a screen reader (and a test) reaches.
              SelectDisplayProps={{ "aria-label": "replace or add to the current sectors" }}
              sx={{ fontSize: 12, ml: 0.5, "& .MuiSelect-select": { py: 0.25 } }}
            >
              <MenuItem value="replace" sx={{ fontSize: 12 }}>
                replace
              </MenuItem>
              <MenuItem value="add" sx={{ fontSize: 12 }}>
                add to
              </MenuItem>
            </Select>
          ) : null}
        </Box>
      </DiffRow>

      <DiffRow label="Regions">
        {regions.changed ? (
          <Typography sx={{ fontSize: 12 }}>
            <Muted>{regionLabel(regions.before)}</Muted>
            {" → "}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {regionLabel(regions.after)}
            </Box>
          </Typography>
        ) : (
          <Muted>unchanged — {regionLabel(regions.after)}</Muted>
        )}
      </DiffRow>

      <DiffRow label="Goal">
        {goal.changed ? (
          <Typography sx={{ fontSize: 12 }}>{goal.after}</Typography>
        ) : (
          <Muted>unchanged</Muted>
        )}
      </DiffRow>

      {preview.warnings.map((w) => (
        <Typography
          key={w}
          role="note"
          sx={{ fontSize: 11, color: preview.applicable ? "text.secondary" : "error.main" }}
        >
          {w}
        </Typography>
      ))}
    </Box>
  );
}

/** An empty region list means the whole world — never render it as "". */
export function regionLabel(regions: string[]): string {
  if (regions.length === 0) return "worldwide";
  if (regions.length <= 3) return regions.join(", ");
  return `${regions.slice(0, 2).join(", ")} + ${regions.length - 2} more`;
}

function DiffRow({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "72px 1fr", gap: 1, alignItems: "start" }}>
      <Typography
        sx={{ fontSize: 11, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.04em", pt: 0.25 }}
      >
        {label}
      </Typography>
      <Box>{children}</Box>
    </Box>
  );
}

function SectorChip({
  label,
  kind,
}: {
  label: string;
  kind: "added" | "removed" | "kept";
}): JSX.Element {
  const prefix = kind === "added" ? "⊕ " : kind === "removed" ? "⊖ " : "";
  return (
    <Chip
      size="small"
      label={`${prefix}${label}`}
      sx={{
        fontSize: 11,
        height: 22,
        ...(kind === "added"
          ? { bgcolor: "success.light", color: "success.contrastText" }
          : kind === "removed"
            ? {
                bgcolor: "transparent",
                border: 1,
                borderColor: "error.light",
                color: "error.main",
                textDecoration: "line-through",
              }
            : { bgcolor: "action.hover", color: "text.secondary" }),
      }}
    />
  );
}

function Muted({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <Box component="span" sx={{ fontSize: 12, color: (t: Theme) => t.palette.text.disabled }}>
      {children}
    </Box>
  );
}
